/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2025 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import QtyField, { QtyFieldStyles } from "@eisinc/App/Components/Qty/QtyField";
import useAccessibleSubmit from "@eisinc/App/Hooks/useAccessibleSubmit";
import { UnitOfMeasureData } from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
    // Xng-Change
    showLabel?: boolean;
    qtyDataName?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
    // XNG-Change
    qtyFieldStyles?: QtyFieldStyles;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    editableQuantityTextField: {
        labelProps: {
            variant: "legend",
            css: css`
                font-size: 10px;
                text-transform: uppercase;
                font-weight: 600;
            `,
        },
    },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    // Xng-Change
    showLabel,
    qtyDataName,
}) => {
    // <xng-change>
    const [allowFractionalQty, setAllowFractionalQty] = React.useState(false);
    const [minQty, setMinQty] = React.useState(1);
    const [multipleSaleQty, setMultipleSaleQty] = React.useState(1);

    React.useEffect(() => {
        if (cartLine) {
            const isFractionalMoq = cartLine.properties?.isFractionalMoq?.toLowerCase() === "true";
            setAllowFractionalQty(isFractionalMoq);

            const unitOfMeasuresData: UnitOfMeasureData[] = JSON.parse(cartLine.properties?.unitOfMeasureData || "[]");
            if (unitOfMeasuresData && cartLine?.unitOfMeasure) {
                const selectedUOM = unitOfMeasuresData.filter(uom => uom.UnitOfMeasure === cartLine.unitOfMeasure);
                let minQty;
                let msq;
                if (selectedUOM.length) {
                    minQty = parseFloat(selectedUOM[0].MinimumOrderQuantity) || 1;
                    msq = parseFloat(selectedUOM[0].MultipleSaleQuantity) || 1;
                } else {
                    minQty = parseFloat(cartLine.properties?.minimumOrderQty) || 1;
                    msq = parseFloat(cartLine.properties?.multipleSaleQty) || 1;
                }

                const isFractionalMoq = !Number.isInteger(minQty);
                setAllowFractionalQty(isFractionalMoq);

                setMinQty(minQty);
                setMultipleSaleQty(msq);
            }
        }
    }, [cartLine]);
    // </xng-change>

    const localOnQtyOrderedChange = (value: string, onError?: () => void) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            onError?.();
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        // Xng-Change
        qtyDataName ? (cartLine as any)[qtyDataName]!.toString() : cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
        // Xng-Change
        allowFractionalQty,
        minQty?.toString(),
        multipleSaleQty?.toString(),
    );

    const [styles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));

    if (editable && !isOutOfStock(cartLine)) {
        return (
            // Xng-Change - use QtyField
            <QtyField
                showLabel={showLabel}
                value={value}
                label={!editable ? label || translate("QTY") : ""}
                data-test-selector="cartline_qty"
                // xng-change - EIS-4192 - adding aria-label for accessibility
                aria-label={translate("QTY")}
                maxLength={9}
                min={0}
                max={999999}
                onKeyDown={keyDownHandler}
                onChange={changeHandler}
                onBlur={blurHandler}
                onClick={event => event.currentTarget.focus()}
                disabled={
                    !cart?.canModifyOrder || cartLine?.isPromotionItem || cart?.type === "Job" || isLoading[cart.id]
                }
                allowFractionalQty={allowFractionalQty}
                multipleSaleQty={multipleSaleQty}
                minimumOrderQty={minQty}
                extendedStyles={extendedStyles?.qtyFieldStyles}
            />
        );
    }

    // Xng-Change
    let qty = cartLine.properties["qtyOrdered"] ? cartLine.properties["qtyOrdered"] : cartLine["qtyOrdered"]!;

    if (qtyDataName) {
        qty = cartLine.properties[qtyDataName] ? cartLine.properties[qtyDataName] : (cartLine as any)[qtyDataName]!;
    }

    // Xng-Change
    return (
        <SmallHeadingAndText
            extendedStyles={styles.readOnlyQuantityText}
            heading={showLabel ? label || translate("QTY") : ""}
            text={qty}
            data-test-selector="quoteDetail_cardLine_qty"
        />
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
